import React from 'react'
import { CaretRight, X } from '@phosphor-icons/react'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Icon, IconButton } from '@shopflo/ui'
import { classNames } from 'src/lib/utils/helpers'
import { currencyFormatter } from 'src/lib/utils/formatters'
import { PlanBadge } from 'src/lib/utils/ui/UpsellPlanBadge'
import { useLocale } from 'src/lib/hooks/useLocal'
import type { UpsellCancellationConfig, UpsellPlanId } from 'src/lib/types/appsIntegrations'

interface UpsellCancellationWarningDialogProps {
  state: {
    isOpen: boolean
    isLoading?: boolean
    currentPlan: UpsellPlanId | null
    litePlanPrice: number
    /** Title, description and lost-feature list from the Upsell app's metadata (shopfloApps). */
    config?: UpsellCancellationConfig
  }
  actions: {
    onClose: () => void
    onContinue: () => void
    onSwitchToLite: () => void
  }
}

const UpsellCancellationWarningDialog: React.FC<UpsellCancellationWarningDialogProps> = ({ state, actions }) => {
  const { isOpen, isLoading = false, currentPlan, litePlanPrice, config } = state
  const { onClose, onContinue, onSwitchToLite } = actions
  const { t } = useLocale()
  const lostFeatures = config?.downgrade_pitch ?? []
  return (
    <Dialog open={isOpen} onOpenChange={open => !open && !isLoading && onClose()}>
      <DialogContent className='bg-white w-[560px] max-w-[calc(100vw-2rem)] !rounded-2xl !p-0 !gap-0'>
        <DialogHeader className='px-5 pt-4 pb-0'>
          <div className='flex items-center justify-between gap-4'>
            <DialogTitle className='flex flex-col items-start gap-1'>
              <p className='text-zinc-800 text-base'>{config?.title}</p>
              <p className='text-zinc-500 text-xs font-medium leading-[18px]'>{config?.description}</p>
            </DialogTitle>
            <IconButton
              variant='ghost'
              size='sm'
              IconComponent={X}
              aria-label='Close'
              className='-mr-1.5 text-zinc-500'
              onClick={onClose}
              disabled={isLoading}
            />
          </div>
        </DialogHeader>

        <div className='px-5'>
          <div className='pt-4 pb-5 flex flex-col'>
            {lostFeatures.map((feature, index) => (
              <div
                key={`${feature.title}-${index}`}
                className={classNames(
                  'flex items-center gap-3 py-4',
                  index === 0 ? 'pt-0' : '',
                  index < lostFeatures.length - 1 ? 'border-b border-zinc-100' : 'pb-0'
                )}
              >
                <img src={feature.icon} alt='' aria-hidden className='h-5 w-5 flex-shrink-0' />
                <div>
                  <p className='text-text-xs-medium text-zinc-800'>{feature.title}</p>
                  <p className='text-text-xs-regular text-zinc-500'>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {currentPlan === 'plus' && (
            <div className='mt-3 flex items-center justify-between gap-3 rounded-lg bg-sky-50 px-2 py-1.5 text-left'>
              <p className='text-xs text-zinc-600 font-medium'>
                Keep your upsells running for less at{' '}
                <span className='font-semibold text-zinc-800'>{currencyFormatter(litePlanPrice)}/mo</span> with{' '}
                <PlanBadge plan='lite' />
              </p>
              <button type='button' onClick={onSwitchToLite} disabled={isLoading}>
                <span className='flex items-center gap-0.5 text-xs font-semibold  text-sky-800 whitespace-nowrap'>
                  {t('upsell_cancellation_switch_to_lite')}
                  <Icon IconComponent={CaretRight} size='xxs' iconProps={{ weight: 'bold' }} />
                </span>
              </button>
            </div>
          )}
        </div>

        <DialogFooter className='px-5 pb-5 pt-3 flex items-center gap-2'>
          <Button variant='outline' size='md' className='flex-1 !mr-0' onClick={onContinue} disabled={isLoading}>
            {t('upsell_cancellation_uninstall_anyway')}
          </Button>
          <Button variant='primary' size='md' className='flex-1 !mr-0' onClick={onClose} disabled={isLoading}>
            {t('upsell_cancellation_keep_using')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default UpsellCancellationWarningDialog
